// Question bank curation — R-51: Group Admins and above can curate a custom
// question bank at group or org level, and can suppress specific default questions.
// R-266: multi-select tone filter. R-282: multi-select principle filter.
// R-283: Add custom question modal.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  listQuestions,
  archiveQuestion,
  createQuestion,
  type Question,
} from '../lib/api';

const PRINCIPLE_LABELS: Record<string, string> = {
  'felt-safety':    'Felt-safety',
  'connection':     'Connection',
  'self-regulation':'Self-regulation',
  'felt_safety':    'Felt-safety',
  'self_regulation':'Self-regulation',
};

const DEPTH_LABELS: Record<number, string> = { 1: 'Surface', 2: 'Medium', 3: 'Deep' };
const DURATION_LABELS: Record<string, string> = { quick: 'Quick', '5min': '5 min', '10min': '10 min' };

/** R-261: tone 1 = lighthearted icebreaker, 5 = deep/personal work question. */
const TONE_LABELS: Record<number, string> = {
  1: 'Icebreaker',
  2: 'Light',
  3: 'Moderate',
  4: 'Deep',
  5: 'Personal',
};

const TONE_COLORS: Record<number, string> = {
  1: '#22c55e',
  2: '#84cc16',
  3: '#f59e0b',
  4: '#f97316',
  5: '#ef4444',
};

// Option lists for the multi-select dropdowns
const TONE_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
  value: n,
  label: `Tone ${n} · ${TONE_LABELS[n]}`,
  chipLabel: String(n),
}));

const PRINCIPLE_OPTIONS = [
  { value: 'felt_safety',     label: 'Felt-safety',     chipLabel: 'Felt-safety' },
  { value: 'connection',      label: 'Connection',      chipLabel: 'Connection' },
  { value: 'self_regulation', label: 'Self-regulation', chipLabel: 'Self-regulation' },
];

// ---------------------------------------------------------------------------
// MultiSelectDropdown — R-266, R-282
// ---------------------------------------------------------------------------
function MultiSelectDropdown<T extends string | number>({
  placeholder,
  prefix = '',
  options,
  selected,
  onChange,
  ariaLabel,
}: {
  placeholder: string;
  prefix?: string;
  options: { value: T; label: string; chipLabel?: string }[];
  selected: Set<T>;
  onChange: (next: Set<T>) => void;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const chipLabels = options
    .filter((o) => selected.has(o.value))
    .map((o) => o.chipLabel ?? o.label);

  const triggerLabel =
    selected.size === 0 ? placeholder : prefix + chipLabels.join(', ');

  function toggle(value: T) {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next);
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className={`btn btn--sm ${selected.size > 0 ? 'btn--primary' : 'btn--secondary'}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        {triggerLabel}
        <span aria-hidden="true" style={{ fontSize: '0.6rem', marginLeft: 2 }}>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          aria-label={ariaLabel}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 200,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 'var(--radius)',
            boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)',
            padding: 'var(--space-2)',
            minWidth: 190,
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          {options.map((opt) => {
            const checked = selected.has(opt.value);
            return (
              <label
                key={String(opt.value)}
                role="option"
                aria-selected={checked}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: '5px var(--space-2)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  userSelect: 'none',
                  background: checked ? 'var(--color-accent-light)' : 'transparent',
                  color: checked ? 'var(--color-accent)' : 'var(--color-text)',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(opt.value)}
                  style={{ accentColor: 'var(--color-accent)' }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddQuestionModal — R-283
// ---------------------------------------------------------------------------
function AddQuestionModal({
  orgId,
  onAdd,
  onClose,
}: {
  orgId: string;
  onAdd: (q: Question) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [principle, setPrinciple] = useState('connection');
  const [tone, setTone] = useState(2);
  const [duration, setDuration] = useState<'quick' | '5min' | '10min'>('quick');
  const [purpose, setPurpose] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const q = await createQuestion(orgId, {
        text: text.trim(),
        purpose: purpose.trim() || 'Custom question',
        principle,
        depth: 1,
        tone,
        duration,
        source: 'manual',
      });
      onAdd(q);
      onClose();
    } catch {
      setError('Failed to save. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    // Backdrop
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-q-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 500,
        background: 'rgb(0 0 0 / 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)',
      }}
    >
      <div
        className="card"
        style={{ width: '100%', maxWidth: 520, maxHeight: '90dvh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="add-q-title" style={{ marginBottom: 'var(--space-5)' }}>
          Add custom question
        </h2>

        <form onSubmit={handleSubmit} noValidate>
          {/* Question text */}
          <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
            <label className="form-label" htmlFor="aq-text">
              Question text <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <textarea
              id="aq-text"
              className="form-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={500}
              rows={3}
              required
              autoFocus
              placeholder="e.g. What's something you're looking forward to this week?"
              style={{ resize: 'vertical' }}
            />
            <p className="form-hint">{text.length}/500 characters</p>
          </div>

          {/* Purpose (optional) */}
          <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
            <label className="form-label" htmlFor="aq-purpose">
              Purpose <span style={{ fontWeight: 400, color: 'var(--color-text-subtle)' }}>(optional)</span>
            </label>
            <input
              id="aq-purpose"
              type="text"
              className="form-input"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="e.g. Builds connection by sharing anticipation"
            />
          </div>

          {/* Principle, Tone, Duration — row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-5)',
            }}
          >
            <div className="form-group">
              <label className="form-label" htmlFor="aq-principle">Principle</label>
              <select
                id="aq-principle"
                className="form-input"
                value={principle}
                onChange={(e) => setPrinciple(e.target.value)}
              >
                <option value="felt_safety">Felt-safety</option>
                <option value="connection">Connection</option>
                <option value="self_regulation">Self-regulation</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="aq-tone">Tone (R-260)</label>
              <select
                id="aq-tone"
                className="form-input"
                value={tone}
                onChange={(e) => setTone(Number(e.target.value))}
              >
                <option value={1}>1 · Icebreaker</option>
                <option value={2}>2 · Light</option>
                <option value={3}>3 · Moderate</option>
                <option value={4}>4 · Deep</option>
                <option value={5}>5 · Personal</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="aq-duration">Duration</label>
              <select
                id="aq-duration"
                className="form-input"
                value={duration}
                onChange={(e) => setDuration(e.target.value as 'quick' | '5min' | '10min')}
              >
                <option value="quick">Quick</option>
                <option value="5min">5 min</option>
                <option value="10min">10 min</option>
              </select>
            </div>
          </div>

          {error && (
            <p
              role="alert"
              style={{
                color: 'var(--color-danger)',
                fontSize: '0.875rem',
                marginBottom: 'var(--space-4)',
              }}
            >
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={submitting || !text.trim()}
            >
              {submitting ? 'Saving…' : 'Add question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuestionBankPage
// ---------------------------------------------------------------------------
export function QuestionBankPage() {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'custom' | 'default' | 'generated'>('all');
  const [toneFilters, setToneFilters] = useState<Set<number>>(new Set());
  const [principleFilters, setPrincipleFilters] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    if (!user) return;
    listQuestions(user.orgId)
      .then((q) => { setQuestions(q); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user]);

  if (!user) return null;

  const visible = questions.filter((q) => {
    if (q.archivedAt) return false;
    if (filter === 'custom' && q.source !== 'manual') return false;
    if (filter === 'default' && q.source !== 'default') return false;
    if (filter === 'generated' && q.source !== 'generated') return false;
    if (principleFilters.size > 0 && !principleFilters.has(q.principle)) return false;
    if (toneFilters.size > 0 && !toneFilters.has(q.tone)) return false;
    return true;
  });

  async function handleSuppress(q: Question) {
    if (!confirm(`Remove "${q.text.slice(0, 60)}…" from your org's bank?`)) return;
    await archiveQuestion(user!.orgId, q.id);
    setQuestions((prev) =>
      prev.map((x) => x.id === q.id ? { ...x, archivedAt: new Date().toISOString() } : x),
    );
  }

  function handleQuestionAdded(q: Question) {
    setQuestions((prev) => [q, ...prev]);
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Question Bank</h1>
          <p className="page-subtitle">
            {visible.length} question{visible.length !== 1 ? 's' : ''} · curate the check-in and connection question pool; AI adds new questions nightly (R-51, R-285)
          </p>
        </div>
        <button
          className="btn btn--primary"
          onClick={() => setShowAddModal(true)}
        >
          + Add custom question
        </button>
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-5)',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <fieldset style={{ border: 'none', display: 'flex', gap: 'var(--space-2)' }}>
          <legend className="sr-only">Filter by source</legend>
          {(['all', 'default', 'generated', 'custom'] as const).map((f) => (
            <button
              key={f}
              className={`btn btn--sm ${filter === f ? 'btn--primary' : 'btn--secondary'}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'default' ? 'Default' : f === 'generated' ? 'AI-generated' : 'Custom'}
            </button>
          ))}
        </fieldset>

        <MultiSelectDropdown
          placeholder="All principles"
          options={PRINCIPLE_OPTIONS}
          selected={principleFilters}
          onChange={setPrincipleFilters}
          ariaLabel="Filter by principle"
        />

        <MultiSelectDropdown
          placeholder="All tones"
          prefix="Tone "
          options={TONE_OPTIONS}
          selected={toneFilters}
          onChange={setToneFilters}
          ariaLabel="Filter by tone"
        />
      </div>

      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}

      {!loading && visible.length === 0 && (
        <div className="empty-state">
          <p className="empty-state__title">No questions match</p>
          <p className="empty-state__body">Try adjusting the filters, or add a custom question.</p>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {visible.map((q) => (
            <div
              key={q.id}
              className="card"
              style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start' }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontWeight: 500, fontSize: '0.9375rem', lineHeight: 1.4 }}>
                  {q.text}
                </p>
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginTop: 'var(--space-2)' }}>
                  {q.purpose}
                </p>
                <div
                  style={{
                    marginTop: 'var(--space-3)',
                    display: 'flex',
                    gap: 'var(--space-2)',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <span className="badge badge--accent">
                    {PRINCIPLE_LABELS[q.principle] ?? q.principle}
                  </span>
                  <span
                    className="badge"
                    style={{
                      backgroundColor: `${TONE_COLORS[q.tone] ?? '#94a3b8'}22`,
                      color: TONE_COLORS[q.tone] ?? '#94a3b8',
                      border: `1px solid ${TONE_COLORS[q.tone] ?? '#94a3b8'}44`,
                    }}
                  >
                    Tone {q.tone} · {TONE_LABELS[q.tone] ?? '—'}
                  </span>
                  <span className="badge badge--neutral">
                    Depth {DEPTH_LABELS[q.depth] ?? q.depth}
                  </span>
                  <span className="badge badge--neutral">
                    {DURATION_LABELS[q.duration] ?? q.duration}
                  </span>
                  {q.source === 'generated' && (
                    <span className="badge badge--accent">AI-generated</span>
                  )}
                  {q.source === 'manual' && (
                    <span className="badge badge--success">Custom</span>
                  )}
                </div>
              </div>
              {q.source !== 'default' && (
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => handleSuppress(q)}
                  style={{ color: 'var(--color-danger)', flexShrink: 0 }}
                  aria-label={`Remove: ${q.text.slice(0, 40)}`}
                >
                  Remove
                </button>
              )}
              {q.source === 'default' && (
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => handleSuppress(q)}
                  style={{ color: 'var(--color-text-subtle)', flexShrink: 0 }}
                  aria-label={`Suppress: ${q.text.slice(0, 40)}`}
                >
                  Suppress
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddQuestionModal
          orgId={user.orgId}
          onAdd={handleQuestionAdded}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </>
  );
}
